import { IPlanificationProgressChart } from '@sdk/contracts';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { DefaultTheme } from 'styled-components';

interface Params {
  planificationProgress: IPlanificationProgressChart | undefined;
  colors: DefaultTheme['colors'];
  t: TFunction<'StudentDashboardTranslations'>;
}

export const useChartData = ({ planificationProgress, colors, t }: Params) => {
  const chartData = useMemo(() => {
    if (!planificationProgress) return null;

    // We need a canvas context to build gradient background
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const gradient = ctx.createLinearGradient(0, 0, 0, 150);
    gradient.addColorStop(0, '#D7DBFC');
    gradient.addColorStop(1, '#F8F9FE');

    const createColoredSegments = () => {
      const segments: Array<{ data: (number | null)[]; color: string; startIndex: number }> = [];
      let currentSegment: { data: (number | null)[]; color: string; startIndex: number } = {
        data: [],
        color: '',
        startIndex: 0
      };

      for (let i = 0; i < planificationProgress.current.length; i++) {
        const actualValue = planificationProgress.current[i];
        const plannedValue = planificationProgress.planned[i];

        // Once nulls appear we stop (future values)
        if (actualValue === null) break;

        const isAbove = actualValue >= plannedValue;
        const segmentColor = isAbove ? colors.green6 : colors.red6;

        if (i === 0 || currentSegment.color !== segmentColor) {
          if (currentSegment.data.length > 0) {
            segments.push(currentSegment);
          }

          currentSegment = {
            data: new Array(planificationProgress.current.length).fill(null) as (number | null)[],
            color: segmentColor,
            startIndex: i
          };

          // Include previous point to connect segments smoothly
          if (i > 0) {
            currentSegment.data[i - 1] = planificationProgress.current[i - 1];
          }
        }

        currentSegment.data[i] = actualValue;
      }

      if (currentSegment.data.length > 0) {
        segments.push(currentSegment);
      }

      return segments;
    };

    const coloredSegments = createColoredSegments();

    const lastActualIndex =
      planificationProgress.current.findIndex((value: number | null) => value === null) - 1;
    const actualLastIndex =
      lastActualIndex >= 0 ? lastActualIndex : planificationProgress.current.length - 1;

    const realDatasets = coloredSegments.map(segment => ({
      label: t('REALIZED'),
      data: segment.data,
      borderColor: segment.color,
      backgroundColor: 'transparent',
      borderWidth: 2,
      fill: false,
      tension: 0.4,
      pointRadius: segment.data.map((value, pointIndex) =>
        value !== null && pointIndex === actualLastIndex ? 6 : 0
      ),
      pointHoverRadius: 4,
      pointBackgroundColor: segment.color,
      pointBorderColor: colors.white,
      pointBorderWidth: segment.data.map((value, pointIndex) =>
        value !== null && pointIndex === actualLastIndex ? 2 : 0
      ),
      spanGaps: false
    }));

    return {
      labels: planificationProgress.labels.map(date => {
        const dateObj = new Date(date);
        return `${dateObj.getDate()}/${dateObj.getMonth() + 1}`;
      }),
      datasets: [
        ...realDatasets,
        {
          label: t('PLANNED'),
          data: planificationProgress.planned,
          borderColor: colors.purple4,
          backgroundColor: gradient,
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: colors.purple4
        }
      ]
    };
  }, [planificationProgress, colors, t]);

  return chartData;
};
